import type React from "react";
import type { Country, SeaDetails } from "../../types";
import type { OrderFormValues } from "../../types/cmrpreview";

type CountryCode = { _id: string } | string | undefined | null;

const BL_TYPE_LABELS: Record<string, string> = {
  ORIGINAL: "Original",
  SEAWAY: "Seaway Bill",
  TELEX: "Telex Release",
};

const ORIGINALS_LABELS: Record<string, string> = {
  "1": "ONE (1)",
  "2": "TWO (2)",
  "3": "THREE (3)",
};

/**
 * Live House Bill of Lading preview — the SEA counterpart of CmrPreview.
 * Black & white document layout mirroring the printed HBL: shipper = pickup,
 * consignee = delivery, plus the HBL block (B/L no/date, originals, type,
 * freight terms, places, notify party) from seaDetails. Place of delivery
 * falls back to the delivery address when the explicit field is empty.
 */
export const BolPreview: React.FC<{
  shipment: OrderFormValues;
  seaDetails?: SeaDetails;
  countries: Country[];
}> = ({ shipment, seaDetails, countries }) => {
  const countryOptions = (countries ?? []).flat().map((country) => ({
    value: country._id,
    label: country.name,
  }));

  const getCountryName = (codeOrName: CountryCode): string => {
    if (!codeOrName) return "";
    const code = typeof codeOrName === "string" ? codeOrName : codeOrName._id;
    if (!code) return "";
    const found = countryOptions.find(
      (c) => c.value?.toLowerCase?.() === code.toLowerCase?.(),
    );
    return found?.label || "";
  };

  // DD.MM.YYYY to match the printed document.
  const fmtDate = (d?: string) => {
    if (!d) return "";
    const date = new Date(d);
    if (isNaN(date.getTime())) return d.split("T")[0];
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.${date.getFullYear()}`;
  };

  const cargo = shipment?.cargoItems ?? [];
  const totalPackages = cargo.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
  const totalWeight = cargo.reduce((s, i) => s + (Number(i.weightKg) || 0), 0);
  const totalVolume = cargo.reduce((s, i) => s + (Number(i.volumeM3) || 0), 0);

  const blRemarkTexts = (seaDetails?.blRemarks ?? [])
    .filter((r) => r?.text?.trim())
    .map((r) => (r.text as string).trim());

  const notify = seaDetails?.notifyParty;
  const notifyLines = [
    notify?.company,
    notify?.contact,
    notify?.email,
    notify?.phone,
  ].filter(Boolean) as string[];

  const placeOfDelivery =
    seaDetails?.placeOfDelivery ||
    [
      shipment?.delivery?.address?.city,
      getCountryName(shipment?.delivery?.address?.country),
    ]
      .filter(Boolean)
      .join(", ");

  const partyLines = (party?: OrderFormValues["pickup"]): string => {
    const lines = [
      party?.companyName,
      party?.address?.street?.split(",")[0],
      [party?.address?.postalCode, party?.address?.city]
        .filter(Boolean)
        .join(" "),
      getCountryName(party?.address?.country),
    ].filter(Boolean);
    return lines.join("\n");
  };

  // A labelled cell. `value` renders as plain pre-line text; `children` lets a
  // caller compose richer content (e.g. the split B/L No. + date cell).
  const Cell: React.FC<{
    label: string;
    value?: string;
    children?: React.ReactNode;
    className?: string;
    minH?: string;
  }> = ({ label, value, children, className, minH }) => (
    <div className={`border border-black p-2 ${minH ?? ""} ${className ?? ""}`}>
      <div className="text-[9px] uppercase tracking-wide text-gray-600">
        {label}
      </div>
      {children ?? (
        <div className="text-xs font-medium whitespace-pre-line mt-0.5">
          {value || ""}
        </div>
      )}
    </div>
  );

  return (
    <div className="break-words text-black">
      {/* Title banner */}
      <div className="border border-black bg-gray-100 text-center py-1.5">
        <h2 className="font-bold text-sm tracking-wide">HOUSE BILL OF LADING</h2>
      </div>

      {/* Shipper | B/L No. + B/L date */}
      <div className="grid grid-cols-5 -mt-px">
        <Cell
          label="Shipper / Exporter"
          value={partyLines(shipment?.pickup)}
          className="col-span-3 -mr-px"
          minH="min-h-[64px]"
        />
        <div className="col-span-2 border border-black p-2 min-h-[64px]">
          <div className="text-[9px] uppercase tracking-wide text-gray-600">
            B/L No.
          </div>
          <div className="text-xs font-bold mt-0.5">
            {seaDetails?.blNumber || ""}
          </div>
          <div className="text-[9px] uppercase tracking-wide text-gray-600 mt-1">
            B/L date
          </div>
          <div className="text-xs font-medium mt-0.5">
            {fmtDate(seaDetails?.blDate)}
          </div>
        </div>
      </div>

      {/* Consignee | No. of originals + B/L type */}
      <div className="grid grid-cols-5 -mt-px">
        <Cell
          label="Consignee"
          value={partyLines(shipment?.delivery)}
          className="col-span-3 -mr-px"
          minH="min-h-[64px]"
        />
        <div className="col-span-2 border border-black p-2 min-h-[64px]">
          <div className="text-[9px] uppercase tracking-wide text-gray-600">
            No. of original B/Ls
          </div>
          <div className="text-xs font-bold mt-0.5">
            {seaDetails?.numberOfOriginals
              ? ORIGINALS_LABELS[String(seaDetails.numberOfOriginals)] ||
                String(seaDetails.numberOfOriginals)
              : ""}
          </div>
          <div className="text-[9px] uppercase tracking-wide text-gray-600 mt-1">
            B/L type
          </div>
          <div className="text-xs font-medium mt-0.5">
            {seaDetails?.blType
              ? BL_TYPE_LABELS[seaDetails.blType] ?? seaDetails.blType
              : ""}
          </div>
        </div>
      </div>

      {/* Notify party */}
      <div className="grid grid-cols-5 -mt-px">
        <Cell
          label="Notify party"
          value={notifyLines.join("\n")}
          className="col-span-3 -mr-px"
          minH="min-h-[40px]"
        />
        <div className="col-span-2 border border-black min-h-[40px]" />
      </div>

      {/* Place of receipt | Port of loading | Vessel/Voyage */}
      <div className="grid grid-cols-3 -mt-px">
        <Cell
          label="Place of receipt"
          value={seaDetails?.placeOfReceipt}
          className="-mr-px"
          minH="min-h-[44px]"
        />
        <Cell
          label="Port of loading"
          value={seaDetails?.portOfLoading}
          className="-mr-px"
          minH="min-h-[44px]"
        />
        <Cell
          label="Vessel / Voyage"
          value={[seaDetails?.vesselName, seaDetails?.voyageNumber]
            .filter(Boolean)
            .join(" / ")}
          minH="min-h-[44px]"
        />
      </div>

      {/* Port of discharge | Place of delivery */}
      <div className="grid grid-cols-2 -mt-px">
        <Cell
          label="Port of discharge"
          value={seaDetails?.portOfDischarge}
          className="-mr-px"
          minH="min-h-[44px]"
        />
        <Cell
          label="Place of delivery"
          value={placeOfDelivery}
          minH="min-h-[44px]"
        />
      </div>

      {/* Cargo details */}
      <div className="border border-black -mt-px p-2 min-h-[90px]">
        <div className="text-[9px] uppercase tracking-wide text-gray-600">
          Cargo details
        </div>
        <div className="text-[9px] italic text-gray-500 mb-1">
          Marks / No. of packages / Description of goods / Gross weight / Volume
        </div>
        <div className="text-xs whitespace-pre-line">
          {cargo
            .map((i) =>
              [
                i.marks,
                [i.quantity, i.type].filter(Boolean).join(" "),
                i.description,
                i.weightKg ? `${i.weightKg} kg` : "",
                i.volumeM3 ? `${i.volumeM3} m³` : "",
              ]
                .filter(Boolean)
                .join("  ·  "),
            )
            .filter(Boolean)
            .join("\n")}
        </div>
        {(totalPackages > 0 || totalWeight > 0 || totalVolume > 0) && (
          <div className="text-xs font-semibold mt-1 pt-1 border-t border-gray-300">
            {`Total: ${totalPackages} pkg · ${totalWeight.toLocaleString(
              "en-US",
            )} kg${totalVolume > 0 ? ` · ${totalVolume} m³` : ""}`}
          </div>
        )}
      </div>

      {/* Freight & charges | Remarks */}
      <div className="grid grid-cols-2 -mt-px">
        <Cell
          label="Freight & charges"
          value={seaDetails?.freightPaymentTerms}
          className="-mr-px"
          minH="min-h-[44px]"
        />
        <Cell
          label="Remarks"
          value={blRemarkTexts.join("\n") || shipment?.additionalNotes || ""}
          minH="min-h-[44px]"
        />
      </div>

      {/* Place of issue | Date of issue | Signed by */}
      <div className="grid grid-cols-3 -mt-px">
        <Cell
          label="Place of issue"
          value={seaDetails?.placeOfReceipt || shipment?.pickup?.address?.city}
          className="-mr-px"
          minH="min-h-[44px]"
        />
        <Cell
          label="Date of issue"
          value={fmtDate(seaDetails?.blDate)}
          className="-mr-px"
          minH="min-h-[44px]"
        />
        <Cell
          label="Signed by (carrier / agent)"
          value={
            seaDetails?.forwarder?.companyName || seaDetails?.shippingLine || ""
          }
          minH="min-h-[44px]"
        />
      </div>
    </div>
  );
};

export default BolPreview;
